import {
  addDays,
  endOfDay,
  format,
  isBefore,
  isSameDay,
  startOfDay,
  startOfWeek,
  subDays,
} from 'date-fns';
import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, Repeat } from 'shared/assets/icons';
import {
  DatepickerWithOptions,
  DatePresets,
  IconOptions,
} from 'shared/components/ui/datepicker-with-options';
import { HabitSchedulesMenu } from 'shared/components/ui/habit-schedules-menu';
import { IconButton } from 'shared/components/ui/icon-button';
import { RepeatForm } from 'shared/components/ui/repeat-rrule-form';
import { weekdaysNumberMap } from 'shared/constants';
import { useToday } from 'shared/contexts/today';
import { DeleteHabitDialog } from 'shared/dialogs/delete-habit';
import { HabitScheduleDialog } from 'shared/dialogs/habit-schedule';
import { useActiveSchedule } from 'shared/hooks/use-active-schedule';
import { useKeysDown } from 'shared/hooks/use-keys-down';
import { InteractionType } from 'shared/types/data-type';
import {
  DateFormatOptions,
  TimeFormatOptions,
} from 'shared/types/date-format-options';
import { HabitSchedule } from 'shared/types/habit-schedule';
import { TaskType } from 'shared/types/task-base';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';
import { formatDurationLabel } from 'shared/utils/format-duration-label';
import { getActiveSchedule } from 'shared/utils/get-active-schedule';
import { getPlatformShortcut } from 'shared/utils/get-platform-shortcut';
import { getScheduleLabel } from 'shared/utils/get-schedule-label';
import { getSmartDateLabel } from 'shared/utils/get-smart-date-label';

import { useTaskDetailSubmenu } from '../../hooks/use-task-detail-submenu';
import { useData } from '../../use-data';
import { MetaInteraction } from '../meta-interaction/meta-interaction';
import * as Styled from './meta-deadline.style';

const noop = () => {};

export type MetaDeadlineProps = {
  name: string;
  deadline?: Timestamp | null;
  type: TaskType;
  schedules?: HabitSchedule[] | null;
  dateFormat: DateFormatOptions;
  timeFormat: TimeFormatOptions;
  weekStartsOn: WeekDays;
  onChangeDeadline: (date?: Timestamp) => void;
  onChangeSchedule: (schedules: HabitSchedule[] | null) => void;
};

export const MetaDeadline: React.FC<MetaDeadlineProps> = ({
  name,
  deadline,
  schedules,
  type,
  onChangeDeadline,
  onChangeSchedule,
  weekStartsOn,
  dateFormat,
  timeFormat,
}) => {
  const { t } = useTranslation();
  const today = useToday();
  const buttonRef = useRef<HTMLDivElement>(null);
  const { menuOpen, openMenu, closeMenu } = useTaskDetailSubmenu();
  const { createHabitTargetAllowed, createHabitAllowed, openPremiumDialog } =
    useData();
  const [showDeleteHabitDialog, setShowDeleteHabitDialog] = useState(false);
  const [schedulesMenuOpen, setSchedulesMenuOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<HabitSchedule | null>(
    null,
  );

  const shortcuts = useMemo(
    () => ({
      [DatePresets.Today]: getPlatformShortcut(['Shift', 'T']),
      [DatePresets.Tomorrow]: getPlatformShortcut(['Alt', 'Shift', 'T']),
      [DatePresets.NextWeek]: getPlatformShortcut(['Shift', 'W']),
    }),
    [],
  );

  const {
    menuOpen: repeatFormOpen,
    openMenu: openRepeatForm,
    closeMenu: closeRepeatForm,
  } = useTaskDetailSubmenu();

  const activeSchedule = useActiveSchedule(schedules, today);

  const label = useMemo(() => {
    if (activeSchedule) {
      return getScheduleLabel(activeSchedule, t, true);
    }

    if (!deadline) {
      return;
    }

    return getSmartDateLabel(deadline, today, t, { enableWeekdays: true });
  }, [activeSchedule, deadline, today, t]);

  const subLabel = useMemo(() => {
    if (!activeSchedule) {
      return;
    }

    const { timeslot, completionTarget } = activeSchedule;

    let prefix = '';
    const completionCount = completionTarget?.count ?? 1;

    if (completionCount > 1) {
      prefix = `${t('general.labels.habit.repeat.value.target.per-day.label', {
        count: completionCount,
        label: t('general.labels.habit.repeat.value.target.default.label', {
          count: completionCount,
        }),
      })}, `;
    }

    // When no start time and duration are set show: All day
    if (!timeslot?.startTime && !timeslot?.duration) {
      return `${prefix}${t('general.labels.all-day')}`;
    }

    // When no start time is set show: All day, 2h
    if (!timeslot?.startTime && timeslot?.duration) {
      return `${prefix}${t('general.labels.all-day')}, ${formatDurationLabel(
        timeslot.duration,
        false,
      )}`;
    }

    // When only start time is set show: 15:00
    if (timeslot?.startTime && !timeslot?.duration) {
      const start = new Date();
      start.setHours(0, 0, 0, 0);
      start.setTime(start.getTime() + timeslot.startTime);

      return `${prefix}${format(start, 'HH:mm')}`;
    }

    // When both start time and duration are set show: 09:00-09:30
    if (timeslot?.startTime && timeslot?.duration) {
      const start = new Date();
      start.setHours(0, 0, 0, 0);
      start.setTime(start.getTime() + timeslot.startTime);

      const end = new Date(start.getTime() + timeslot.duration);

      return `${prefix}${format(start, 'HH:mm')} - ${format(end, 'HH:mm')}`;
    }
  }, [activeSchedule, t]);

  const shouldReplaceSchedule = useMemo(
    () =>
      activeSchedule?.startDate && isSameDay(activeSchedule?.startDate, today),
    [activeSchedule?.startDate, today],
  );

  const maxDate = useMemo(() => {
    if (!schedules) {
      return undefined;
    }

    const sortedSchedules = [...schedules].sort(
      (a, b) => a.startDate.getTime() - b.startDate.getTime(),
    );

    const currentIndex = sortedSchedules.findIndex(
      (s) => s === editingSchedule,
    );

    const nextSchedule = sortedSchedules[currentIndex + 1];

    if (!nextSchedule) {
      return undefined;
    }

    return subDays(nextSchedule.startDate, 1);
  }, [schedules, editingSchedule]);

  const minDate = useMemo(() => {
    if (!schedules) {
      return undefined;
    }

    const sortedSchedules = [...schedules].sort(
      (a, b) => a.startDate.getTime() - b.startDate.getTime(),
    );

    const currentIndex = sortedSchedules.findIndex(
      (_schedule) => _schedule === editingSchedule,
    );

    const previousSchedule = sortedSchedules[currentIndex - 1];

    if (!previousSchedule?.endDate) {
      return undefined;
    }

    return addDays(previousSchedule.endDate, 1);
  }, [schedules, editingSchedule]);

  const onScheduleClick = () => {
    if (schedules?.length === 1) {
      setEditingSchedule(schedules[0]);
      return;
    }

    setSchedulesMenuOpen(true);
  };

  const closeMenus = () => {
    closeMenu();
    closeRepeatForm();
    setSchedulesMenuOpen(false);
    setEditingSchedule(null);
  };

  const _onChangeDeadline = (date?: Timestamp) => {
    onChangeDeadline(date);
    closeMenus();
  };

  const _onChangeSchedule = (schedule: HabitSchedule | null) => {
    if (!schedule) {
      onChangeSchedule(null);
      closeMenus();
      return;
    }

    const endedSchedules = schedules?.filter(({ endDate }) => !!endDate) ?? [];
    const updateSchedules = [];
    const activeSchedule = getActiveSchedule(schedules, today);
    const newSchedule = {
      ...schedule,
      timeslot: activeSchedule?.timeslot ?? null,
    };

    if (!shouldReplaceSchedule) {
      const endDate = endOfDay(today);
      !!activeSchedule &&
        updateSchedules.push({
          ...activeSchedule,
          endDate,
          plannedOccurrences: activeSchedule?.plannedOccurrences?.filter(
            ({ date }) => isBefore(date, startOfDay(endDate)),
          ),
        });
    }

    if (shouldReplaceSchedule && activeSchedule) {
      newSchedule.entries = activeSchedule.entries ?? [];
      newSchedule.completions = activeSchedule.completions ?? [];
      newSchedule.skips = activeSchedule.skips ?? null;
    }

    updateSchedules.push(newSchedule);

    onChangeSchedule([...endedSchedules, ...updateSchedules]);
    closeMenus();
  };

  const closeDeleteHabitDialog = () => setShowDeleteHabitDialog(false);

  const deleteSchedules = () => {
    onChangeSchedule(null);
    closeMenus();
    closeDeleteHabitDialog();
  };

  const onClear = () => {
    if (schedules?.length) {
      setShowDeleteHabitDialog(true);
      closeMenus();
      return;
    }

    onChangeDeadline();
    closeMenus();
  };

  useKeysDown(
    ['KeyD'],
    (e) => {
      if (!menuOpen && !repeatFormOpen) {
        e.preventDefault();
        e.stopPropagation();
        openMenu();
      }
    },
    { ignoreInputFields: true, capture: true },
  );

  useKeysDown(
    ['KeyT'],
    (e) => {
      if (menuOpen) {
        return;
      }

      e.preventDefault();
      // If Alt is pressed, set tomorrow, otherwise set today
      if (e.altKey) {
        _onChangeDeadline(endOfDay(addDays(today, 1)));
        return;
      }
      _onChangeDeadline(endOfDay(today));
    },
    { ignoreInputFields: true, otherKeysInCombinationAllowed: true },
  );

  useKeysDown(
    ['KeyW'],
    (e) => {
      if (menuOpen || !e.shiftKey) {
        // Only trigger on Shift+W
        return;
      }

      e.preventDefault();
      _onChangeDeadline(
        endOfDay(
          startOfWeek(addDays(today, 7), {
            weekStartsOn: weekdaysNumberMap[weekStartsOn],
          }),
        ),
      );
    },
    { ignoreInputFields: true, otherKeysInCombinationAllowed: true },
  );

  useKeysDown(
    ['KeyU'],
    (e) => {
      if (menuOpen || !e.shiftKey) {
        // Only trigger on Shift+U
        return;
      }

      e.preventDefault();
      _onChangeDeadline(undefined);
    },
    { ignoreInputFields: true, otherKeysInCombinationAllowed: true },
  );

  const handleSchedulesMenuClose = () => {
    setSchedulesMenuOpen(false);
  };

  const handleScheduleSelect = (schedule: HabitSchedule) => {
    setEditingSchedule(schedule);
    setSchedulesMenuOpen(false);
  };

  const handleHabitScheduleCancel = () => {
    setEditingSchedule(null);
  };

  const handleHabitScheduleSubmit: NonNullable<
    React.ComponentProps<typeof HabitScheduleDialog>['onSubmit']
  > = (schedule, splitOptions) => {
    let updatedSchedules = schedules ?? [];

    if (splitOptions && editingSchedule) {
      const originalUpdated = {
        ...editingSchedule,
        endDate: splitOptions.originalScheduleEndDate,
      };

      updatedSchedules = updatedSchedules.map((_schedule) =>
        _schedule === editingSchedule ? originalUpdated : _schedule,
      );
      updatedSchedules.push(schedule);

      onChangeSchedule(updatedSchedules);
      setEditingSchedule(null);
      return;
    }

    updatedSchedules = updatedSchedules.map((_schedule) =>
      _schedule === editingSchedule ? schedule : _schedule,
    );

    onChangeSchedule(updatedSchedules);
    setEditingSchedule(null);
  };

  return (
    <>
      <Styled.Container>
        <MetaInteraction
          ref={buttonRef}
          icon={schedules ? Repeat : Calendar}
          placeholder={t('dialogs.task-detail.meta.deadline.placeholder')}
          label={label}
          subLabel={subLabel}
          onClick={schedules ? onScheduleClick : openMenu}
          onClear={label ? onClear : undefined}
          selected={menuOpen || repeatFormOpen || schedulesMenuOpen}
        />

        {!schedules && (
          <Styled.IconButtonContainer>
            <IconButton icon={Repeat} onClick={openRepeatForm} />
          </Styled.IconButtonContainer>
        )}

        {repeatFormOpen && (
          <RepeatForm
            initialRrule={activeSchedule?.rrule?.format}
            initialFrequency={activeSchedule?.frequency}
            weekStartsOn={weekStartsOn}
            type={type}
            onChange={_onChangeSchedule}
            onPremium={openPremiumDialog}
            isTargetPremium={!createHabitTargetAllowed}
            isCreateHabitPremium={!createHabitAllowed}
            onClose={closeMenus}
            referenceElement={buttonRef}
            position="bottom-start"
            interactionType={InteractionType.Task}
          />
        )}

        {menuOpen && (
          <DatepickerWithOptions
            referenceElement={buttonRef}
            value={deadline ?? undefined}
            onChange={_onChangeDeadline}
            dateFormat={dateFormat}
            weekStartsOn={weekStartsOn}
            icon={IconOptions.Calendar}
            onOutside={closeMenus}
            canRemoveValue
            shortcuts={shortcuts}
            clearShortcut={getPlatformShortcut(['Shift', 'U'])}
          />
        )}
      </Styled.Container>
      {schedulesMenuOpen && schedules && (
        <HabitSchedulesMenu
          schedules={schedules}
          referenceElement={buttonRef}
          onClose={handleSchedulesMenuClose}
          onSelect={handleScheduleSelect}
        />
      )}

      {!!editingSchedule && (
        <HabitScheduleDialog
          open={!!editingSchedule}
          initialValues={editingSchedule}
          maxDate={maxDate}
          minDate={minDate}
          onCancel={handleHabitScheduleCancel}
          onSubmit={handleHabitScheduleSubmit}
          dateFormat={dateFormat}
          weekStartsOn={weekStartsOn}
          timeFormat={timeFormat}
          createHabitAllowed={createHabitAllowed}
          createHabitTargetAllowed={createHabitTargetAllowed}
          onPremium={openPremiumDialog}
        />
      )}

      {showDeleteHabitDialog && (
        <DeleteHabitDialog
          open={showDeleteHabitDialog}
          name={name}
          onClose={closeDeleteHabitDialog}
          onSubmit={deleteSchedules}
          onRetry={noop}
          isScheduleRemovalOnly
        />
      )}
    </>
  );
};
